import React, { useState, useRef, useEffect } from 'react';
import { 
  ArrowLeft, 
  Camera, 
  Upload, 
  Zap, 
  FileText, 
  Check, 
  AlertCircle,
  BarChart3,
  Timer,
  ScanLine,
  Search,
  Loader2
} from 'lucide-react';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext'; // NUEVO IMPORT
import toast from 'react-hot-toast';

const MAX_FILE_SIZE_MB = 8;

const isPdfFile = (file) =>
  String(file?.type || '').includes('pdf') || String(file?.name || '').toLowerCase().endsWith('.pdf');

const isAllowedFile = (file) =>
  String(file?.type || '').startsWith('image/') || isPdfFile(file);

const formatFileSize = (bytes) => {
  const size = Number(bytes || 0);
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  if (size >= 1024) return `${Math.round(size / 1024)} KB`;
  return `${size} B`;
};

const readFunctionErrorMessage = async (error, response) => {
  const contextResponse = response || error?.context;

  if (contextResponse && typeof contextResponse.clone === 'function') {
    try {
      const json = await contextResponse.clone().json();
      if (json?.error) return json.error;
      if (json?.message) return json.message;
    } catch {
      try {
        const text = await contextResponse.clone().text();
        if (text?.trim()) return text.trim();
      } catch {
        // Seguimos con el mensaje original.
      }
    }
  }

  return error?.message || "Error al conectar con el servidor OCR.";
};

const ComprasOCR = ({ onNavigate }) => {
  const { profile } = useAuth(); // NUEVO: Extraemos profile para usar el ID de la organización
  const [loading, setLoading] = useState(false);
  const [fileData, setFileData] = useState(null);
  const [fileMeta, setFileMeta] = useState(null);
  const fileInputRef = useRef(null);

  // NUEVO: Estados para los KPIs reales
  const [kpiData, setKpiData] = useState({
    today: 0,
    savedMins: 0,
    recent: []
  });
  const [loadingKpis, setLoadingKpis] = useState(true);

  // NUEVO: Función para buscar los logs de uso del OCR en la base de datos
  const fetchKPIs = async () => {
    if (!profile?.organization_id) return;
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const { data: logs, error } = await supabase
        .from('usage_logs')
        .select('id, created_at, button_clicked')
        .eq('organization_id', profile.organization_id)
        .eq('module_id', 'OCR_COMPRAS')
        .order('created_at', { ascending: false });

      if (!error && logs) {
        const todayLogs = logs.filter(l => new Date(l.created_at) >= today);
        setKpiData({
          today: todayLogs.length,
          savedMins: logs.length * 4, // Calculamos 4 minutos ahorrados por cada factura procesada históricamente
          recent: logs.slice(0, 3) // Tomamos los últimos 3
        });
      }
    } catch (error) {
      console.error("Error cargando KPIs:", error);
    } finally {
      setLoadingKpis(false);
    }
  };

  // NUEVO: Ejecutar la búsqueda de KPIs al montar el componente
  useEffect(() => {
    if (profile) {
      fetchKPIs();
    }
  }, [profile]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validación básica de tamaño (ej: máx 4MB para no saturar la Edge Function)
      if (!isAllowedFile(file)) {
          toast.error("Solo se permiten imagenes o archivos PDF.");
          return;
      }

      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
          toast.error(`El archivo es muy pesado. Intenta con uno menor a ${MAX_FILE_SIZE_MB}MB.`);
          return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setFileData(reader.result);
        setFileMeta({
          name: file.name,
          type: file.type || (isPdfFile(file) ? 'application/pdf' : 'image/*'),
          isPdf: isPdfFile(file),
          size: file.size
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleProcess = async () => {
    if (!fileData || !fileMeta) return toast.error("Carga un comprobante primero");
    setLoading(true);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;

      if (!accessToken) {
          throw new Error("No hay sesión activa. Recarga la página.");
      }

      // Llamada a la nueva función dedicada
      console.log("Invocando function: ocr-compras...");
      
      const { data, error, response } = await supabase.functions.invoke('ocr-compras', {
        body: {
          image: fileData,
          fileData,
          fileName: fileMeta.name,
          fileType: fileMeta.type
        },
        headers: { 
            Authorization: `Bearer ${accessToken}`
        },
      });

      // 1. Error de Red / Supabase (ej: 500, 404, CORS)
      if (error) {
          console.error("Error de invocación Supabase:", error);
          const functionMessage = await readFunctionErrorMessage(error, response);
          throw new Error(functionMessage);
      }

      // 2. Error lógico de la función (ej: OpenAI falló, imagen corrupta)
      // La función devuelve { ok: false, error: "..." } si falla internamente
      if (data && data.ok === false) {
          throw new Error(data.error || "La IA no pudo leer el comprobante.");
      }

      // 3. Éxito
      if (data && data.data) {
          toast.success("¡Comprobante leido!");
          console.log("Datos extraídos:", data.data);

          // NUEVO: Guardar el log de uso exitoso para actualizar los KPIs
          try {
            if (profile?.organization_id) {
              await supabase.from('usage_logs').insert({
                organization_id: profile.organization_id,
                user_id: profile.user_id,
                module_id: 'OCR_COMPRAS',
                button_clicked: 'scan_success',
                time_spent_seconds: 5
              });
            }
          } catch (logErr) {
            console.warn("No se pudo registrar log de OCR", logErr);
          }

          // Guardamos en localStorage para pasarlo a la siguiente pantalla
          localStorage.setItem('temp_ocr_data', JSON.stringify(data.data));
          
          // Navegamos
          onNavigate('compras-nueva', data.data);
      } else {
          throw new Error("La respuesta del servidor está vacía o tiene un formato incorrecto.");
      }

    } catch (err) {
      console.error("Error CRÍTICO en OCR:", err);
      // Mostramos el error real al usuario para debug
      toast.error(`Falla: ${err.message}`, { duration: 5000 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full min-h-screen bg-[#F8FAFC] p-4 font-sans text-slate-700 animate-in fade-in duration-500">
      
      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4 px-1">
        <div className="flex items-center gap-3">
            <button
                onClick={() => onNavigate('compras')}
                className="p-2 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-[#51A7FD] shadow-sm transition-all"
                title="Volver"
            >
                <ArrowLeft size={18} />
            </button>
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <Zap size={18} className="text-[#3FF2AA]" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase">OCR de compras</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                Carga inteligente de comprobantes
            </p>
          </div>
        </div>

        {/* Status Badge */}
        <div
            className="hidden md:flex px-3 py-1.5 bg-indigo-50 border border-indigo-100 rounded-lg items-center gap-2"
            title="El lector OCR esta disponible para procesar comprobantes."
        >
            <div className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse"></div>
            <span className="text-[9px] font-black uppercase text-indigo-700 tracking-widest">OCR activo</span>
        </div>
      </div>

      {/* KPIs (MODIFICADOS PARA USAR DATOS REALES) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div
            className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4"
            title="Cantidad de comprobantes procesados hoy con OCR."
        >
            <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-500">
                <ScanLine size={20} />
            </div>
            <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Procesados hoy</p>
                {/* CÓDIGO ORIGINAL: <p className="text-xl font-black text-slate-800">12</p> */}
                <p className="text-xl font-black text-slate-800">
                  {loadingKpis ? <Loader2 className="animate-spin text-slate-300" size={16} /> : kpiData.today}
                </p>
            </div>
        </div>
        <div
            className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4"
            title="Estimacion de tiempo ahorrado usando OCR en compras."
        >
            <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-500">
                <Timer size={20} />
            </div>
            <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Tiempo ahorrado</p>
                {/* CÓDIGO ORIGINAL: <p className="text-xl font-black text-slate-800">~45 min</p> */}
                <p className="text-xl font-black text-slate-800">
                  {loadingKpis ? <Loader2 className="animate-spin text-slate-300" size={16} /> : `~${kpiData.savedMins} min`}
                </p>
            </div>
        </div>
        <div
            className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4"
            title="Precision historica estimada del lector OCR."
        >
            <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center text-purple-500">
                <BarChart3 size={20} />
            </div>
            <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Precision estimada</p>
                <p className="text-xl font-black text-slate-800">99.1%</p>
            </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* AREA DE CARGA */}
        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8 flex flex-col items-center justify-center min-h-[400px] relative overflow-hidden">
            {fileData ? (
                <div className="relative w-full h-full flex items-center justify-center animate-in zoom-in duration-300">
                    {fileMeta?.isPdf ? (
                        <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-slate-50 p-8 text-center shadow-lg">
                            <div className="w-20 h-20 rounded-3xl bg-white border border-slate-200 flex items-center justify-center mx-auto shadow-sm">
                                <FileText size={34} className="text-rose-500" />
                            </div>
                            <p className="mt-4 text-[11px] font-black text-slate-800 uppercase break-all">{fileMeta?.name}</p>
                            <p className="mt-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                PDF listo para OCR • {formatFileSize(fileMeta?.size)}
                            </p>
                        </div>
                    ) : (
                        <img src={fileData} alt="Preview" className="max-h-[350px] object-contain rounded-xl shadow-lg" />
                    )}
                    
                    {!loading && (
                        <button 
                            onClick={() => {
                              setFileData(null);
                              setFileMeta(null);
                              if (fileInputRef.current) fileInputRef.current.value = null;
                            }}
                            className="absolute top-0 right-0 bg-white/80 p-2 rounded-full shadow-sm hover:bg-rose-100 text-rose-500 transition-colors"
                            title="Quitar archivo cargado"
                        >
                            <AlertCircle size={20} />
                        </button>
                    )}

                    {/* Overlay de carga */}
                    {loading && (
                        <div className="absolute inset-0 bg-[#0B2A36]/60 flex flex-col items-center justify-center backdrop-blur-sm rounded-xl z-20">
                             <Loader2 className="text-[#3FF2AA] animate-spin mb-3" size={48} />
                             <p className="text-white font-black uppercase text-xs tracking-widest animate-pulse">Leyendo comprobante...</p>
                             <p className="text-white/60 text-[10px] mt-1 font-mono">Procesando datos de compra</p>
                        </div>
                    )}
                </div>
            ) : (
                <div
                    className="text-center space-y-4 group cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                    title="Selecciona una imagen o PDF del comprobante para leerlo con OCR."
                >
                    <div className="w-24 h-24 bg-slate-50 rounded-full flex items-center justify-center mx-auto border-2 border-dashed border-slate-300 group-hover:border-[#3FF2AA] group-hover:bg-[#3FF2AA]/5 transition-all duration-300">
                        <Camera size={36} className="text-slate-300 group-hover:text-[#3FF2AA] transition-colors" />
                    </div>
                    <div>
                        <h3 className="text-lg font-black text-slate-700 uppercase group-hover:text-[#0B2A36] transition-colors">Subi tu comprobante</h3>
                        <p className="text-xs text-slate-400 mt-2 max-w-xs mx-auto leading-relaxed">
                            Soporta JPG, PNG y PDF. Maximo 8MB.<br/>
                            <span className="text-slate-300">Se detectan items, fechas, proveedor y totales.</span>
                        </p>
                    </div>
                    <div className="flex gap-3 justify-center pt-2">
                        <button
                            className="px-5 py-2.5 bg-[#0B2A36] text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all flex items-center gap-2 group-hover:scale-105"
                            title="Elegir archivo para procesar"
                        >
                            <Upload size={14} /> Seleccionar Archivo
                        </button>
                    </div>
                </div>
            )}
            <input type="file" ref={fileInputRef} className="hidden" accept="image/*,.pdf,application/pdf" onChange={handleFileChange} />
        </div>

        {/* PANEL DERECHO */}
        <div className="space-y-4">
            <div className="bg-[#3FF2AA] rounded-3xl p-6 shadow-lg shadow-emerald-500/10 text-[#0B2A36] relative overflow-hidden">
                <div className="relative z-10">
                    <h2 className="text-2xl font-black tracking-tighter uppercase mb-2">Procesar comprobante</h2>
                    <p className="text-xs font-bold uppercase tracking-widest opacity-80 mb-6">
                        Carga el comprobante y completamos la compra con la informacion detectada.
                    </p>
                    
                    <button 
                        onClick={handleProcess}
                        disabled={!fileData || loading}
                        className="w-full py-4 bg-white rounded-2xl font-black uppercase text-xs tracking-[0.2em] shadow-sm hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:scale-100 disabled:cursor-not-allowed"
                        title={!fileData ? 'Primero carga un comprobante.' : 'Procesar archivo y completar la compra automaticamente.'}
                    >
                        {loading ? 'Procesando...' : 'Leer comprobante'} <Zap size={16} fill="currentColor" />
                    </button>
                </div>
                {/* Elemento decorativo */}
                <Zap size={120} className="absolute -bottom-4 -right-4 text-[#0B2A36] opacity-10 rotate-12" />
            </div>

            <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">Ultimos procesados</h3>
                <div className="space-y-3">
                    
                    {/* CÓDIGO ORIGINAL COMENTADO PARA NO BORRAR NADA
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100 opacity-60">
                        <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center border border-slate-200">
                            <FileText size={14} className="text-slate-400" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <p className="text-[10px] font-black text-slate-700 uppercase">Sin actividad reciente</p>
                        </div>
                    </div> 
                    */}

                    {loadingKpis ? (
                      <div className="flex items-center justify-center py-4 text-slate-300">
                        <Loader2 className="animate-spin" size={16} />
                      </div>
                    ) : kpiData.recent.length === 0 ? (
                      <div
                          className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100 opacity-60"
                          title="Todavia no hay comprobantes procesados recientemente."
                      >
                          <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center border border-slate-200">
                              <FileText size={14} className="text-slate-400" />
                          </div>
                          <div className="min-w-0 flex-1">
                              <p className="text-[10px] font-black text-slate-700 uppercase">Sin actividad reciente</p>
                          </div>
                      </div>
                    ) : (
                      kpiData.recent.map((log) => (
                        <div
                            key={log.id}
                            className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100 transition-all hover:bg-slate-100"
                            title={`Procesado el ${new Date(log.created_at).toLocaleDateString()} a las ${new Date(log.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`}
                        >
                            <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center border border-slate-200 shadow-sm text-[#3FF2AA]">
                                <Check size={14} className="text-[#0B2A36]" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="text-[10px] font-black text-slate-700 uppercase">Comprobante leido</p>
                                <p className="text-[9px] font-bold text-slate-400">
                                    {new Date(log.created_at).toLocaleDateString()} - {new Date(log.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                </p>
                            </div>
                        </div>
                      ))
                    )}
                </div>
            </div>
        </div>
      </div>
    </div>
  );
};

export default ComprasOCR;
